import React, { useState, useEffect } from "react";
import { Edit } from "lucide-react";
import { useLocation } from "react-router-dom";
import "./ProductBenefits.css";
import BaseButton from "../../Common/BaseButton";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useProductContext } from "../../Context/ProductContext";
import { manageAgentSessionIds } from "../../../helpers/index";
import { generateProductDescriptionStream } from "../../../utils/Product";

const ProductBenefit01 = (props) => {
  const {
    initialData,
    isLoadedFromDB,
    language,
    locationFocus,
    keywords: propKeywords,
    agentId: propAgentId,
    sessionId: propSessionId,
    setError,
    previewMode,
  } = props;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [previousResponse, setPreviousResponse] = useState(null);
  const location = useLocation();
  const initialSku = location.state?.sku || "";
  const keywords = propKeywords || location.state?.keywords || [];
  const [isLoading, setIsLoading] = useState(false);
  const { agentId: hookAgentId, sessionId: hookSessionId } =
    manageAgentSessionIds();
  const agentId = propAgentId || hookAgentId;
  const sessionId = propSessionId || hookSessionId;
  const componentType = "benefit";
  const { productData, updateProductData } = useProductContext();

  useEffect(() => {
    if (initialData) {
      if (initialData.headline) {
        setTitle(initialData.headline);
      }
      if (
        initialData.benefits &&
        Array.isArray(initialData.benefits) &&
        initialData.benefits.length > 0
      ) {
        const descriptionText = initialData.benefits
          .map((benefit) => benefit.description || benefit)
          .join("\n");
        setDescription(descriptionText);
      }
      setPreviousResponse(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    if (productData[initialSku]?.benefit) {
      const { title, description } = productData[initialSku].benefit;
      setTitle(title || "");
      setDescription(description || "");
      setPreviousResponse(productData[initialSku].benefit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productData]);

  const handleProductRegenerate = async (section, furtherDetails) => {
    try {
      setIsEditing(false);
      setIsLoading(true);
      setTitle("");
      setDescription("");

      let finalTitle = "";
      let finalDescription = "";

      const languageValue = language?.label || language || "";
      const locationValue = locationFocus?.label || locationFocus || "";

      await generateProductDescriptionStream(
        initialSku,
        keywords,
        section,
        furtherDetails,
        agentId,
        sessionId,
        (chunk) => {
          if (chunk && chunk.benefit) {
            finalTitle = chunk.benefit;
            finalDescription = chunk.description || "";

            setTitle(finalTitle);
            setDescription(finalDescription);
            setIsLoading(false);
          }
        },
        () => {
          // Capture the current response as previous response for next regeneration
          const currentResponse = {
            title: finalTitle,
            description: finalDescription,
          };
          setPreviousResponse(currentResponse);

          updateProductData(initialSku, componentType, currentResponse);
        },
        (err) => {
          setError(err.message || "Streaming error");
          setIsLoading(false);
        },
        previousResponse, // Pass the previous response
        languageValue,
        locationValue
      );
    } catch (error) {
      setError(error.message || "Streaming error");
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    const updatedData = {
      title,
      description,
    };
    updateProductData(initialSku, componentType, updatedData);
    setIsEditing(false);
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const onRegenerate = async (formData) => {
    setIsLoading(true);
    await handleProductRegenerate(componentType, formData, previousResponse);
    setIsLoading(false);
  };

  return (
    <div className={`container py-8 ${previewMode ? "" : "product-section"}`}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        {!previewMode && (
          <>
            <h2 className="h5 mb-0 section-title">Product benefits 01</h2>
            {!isLoading &&
              (isEditing ? (
                <button className="btn btn-dark btn-sm" onClick={handleSave}>
                  Done
                </button>
              ) : (
                <div className="d-flex gap-2">
                  <BaseButton
                    className="outlineprimary"
                    children="Regenerate Text"
                    imageSrc="/regenerate-text.svg"
                    isModalOpen={true}
                    handleClick={onRegenerate}
                  />
                  <BaseButton
                    className="outlineprimary"
                    children="Edit Text"
                    imageSrc="/Edit.svg"
                    handleClick={toggleEdit}
                  />
                </div>
              ))}
          </>
        )}
      </div>

      {isLoading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            padding: "20px",
            border: "1px solid #f5c2c7",
            borderRadius: "10px",
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#fff",
          }}
        >
          <div style={{ flex: 1 }}>
            <Skeleton
              height={35}
              width="70%"
              style={{ marginBottom: "20px" }}
            />
            <Skeleton
              count={3}
              height={12}
              width="90%"
              style={{ marginBottom: "8px" }}
            />
            <Skeleton count={2} height={12} width="80%" />
          </div>
        </div>
      ) : (
        <>
          {isEditing && !previewMode ? (
            <div className=" sectionbgc p-3 rounded">
              <div className="col-12">
                <div className="mb-4">
                  <span className="benefitText">Title</span>
                  <input
                    type="text"
                    className="form-control "
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                <div>
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control "
                    rows="4"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="sectionbgc p-3 rounded ">
                <div className="row">
                  <div className="col-auto d-flex  flex-column  gap-2">
                    {/* <div className="benefits-img"></div> */}
                    <div className="benifitTitleText">{title}</div>
                    <p className="benefits-desc">{description}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ProductBenefit01;
